import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { DependencyCountResponse } from '../../../../shared/dependenciesV2';
import type { DependencySource } from '../../../../shared/vendoredDependencies';

type DependencyCountParams = {
  sources?: DependencySource[];
};

function getDependenciesCount(locator: string, params?: DependencyCountParams) {
  return get<DependencyCountResponse>(`/api/v2/revisions/${nuc(locator)}/dependencies/count`, false, params);
}

export const getDependenciesCountQueryKey = (
  locator: string,
  params?: DependencyCountParams,
): QueryKey => [
  'revisions',
  locator,
  'dependencies',
  'count',
  params,
];

/**
 * Hook to fetch the count of dependencies for a given project locator.
 *
 * @param {string} locator - The locator of the project revision.
 * @param {DependencyCountParams} params - Optional params to filter the count (e.g. by source).
 * @param {boolean} [shouldFetch] - Whether to fetch the count.
 */
export function useDependenciesCount(
  locator: string,
  params?: DependencyCountParams,
  shouldFetch = true,
) {
  return useQuery<DependencyCountResponse>({
    queryFn: () => getDependenciesCount(locator, params),
    queryKey: getDependenciesCountQueryKey(locator, params),
    enabled: shouldFetch && !!locator,
  });
}
